import React from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon, Save as SaveIcon } from "@mui/icons-material";

import useOrganisation from "../../../../hooks/super/useOrganisation";

const TOKENS = {
  primary: "#6371F7",
  primaryHover: "#0244BE",
  primarySoft: "#EBEDF5",
  danger: "#FB3748",
  dangerBg: "#FEECEE",
  success: "#1FC16B",
  fg1: "#292929",
  fg2: "#7B7B7B",
  fg3: "#959595",
  border: "#D4D4D4",
  borderSubtle: "rgba(0,0,0,0.08)",
  bgSurface: "#FFFFFF",
  bgSunken: "#F7F7F9",
  bgOverlay: "rgba(17,17,17,0.5)",
  shadowL: "0 12px 32px rgba(17,17,17,0.08), 0 4px 8px rgba(17,17,17,0.04)",
  shadowFocus: "0 0 0 3px rgba(99,113,247,0.22)",
};

const inputSx = (disabled) => ({
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    fontSize: 13.5,
    color: TOKENS.fg1,
    backgroundColor: disabled ? TOKENS.bgSunken : TOKENS.bgSurface,
    "& fieldset": { borderColor: TOKENS.border },
    "&:hover fieldset": { borderColor: disabled ? TOKENS.border : TOKENS.fg3 },
    "&.Mui-focused fieldset": {
      borderColor: TOKENS.primary,
      borderWidth: "1px",
    },
    "&.Mui-focused": { boxShadow: TOKENS.shadowFocus },
    "&.Mui-disabled": { color: TOKENS.fg3 },
  },
  "& .MuiOutlinedInput-input": { padding: "10px 12px" },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: TOKENS.fg3,
  },
  "& .MuiFormHelperText-root": { marginLeft: 0, fontSize: 11.5 },
});

const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: 12.5,
      fontWeight: 500,
      color: TOKENS.fg2,
      mb: "6px",
      letterSpacing: 0.1,
    }}
  >
    {children}
    {required && <Box component="span" sx={{ color: TOKENS.danger, ml: 0.5 }}>*</Box>}
  </Typography>
);

function EditOrganisationModal({ open, handleClose, orgName }) {
  const { updateOrganisation } = useOrganisation();
  const target = (orgName && orgName[0]) || {};

  return (
    <Formik
      enableReinitialize
      initialValues={{ title: target.title || "" }}
      validationSchema={Yup.object().shape({
        title: Yup.string().max(255).required("Organisation title is required"),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        try {
          await updateOrganisation(target.id, values);
          resetForm();
          handleClose();
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, resetForm }) => {
        const canSubmit =
          values.title.trim() &&
          values.title.trim() !== (target.title || "").trim() &&
          !isSubmitting;

        const onClose = () => {
          resetForm();
          handleClose();
        };

        return (
          <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
              sx: {
                width: 520,
                maxWidth: "92vw",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: TOKENS.shadowL,
                m: 2,
              },
            }}
            BackdropProps={{ sx: { backgroundColor: TOKENS.bgOverlay } }}
          >
            <form noValidate onSubmit={handleSubmit}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: "20px",
                  py: "18px",
                  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 17,
                    fontWeight: 600,
                    color: TOKENS.fg1,
                    letterSpacing: -0.1,
                  }}
                >
                  Edit organisation
                </Typography>
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{ color: TOKENS.fg2, "&:hover": { color: TOKENS.fg1 } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Body */}
              <Box sx={{ px: "24px", py: "20px" }}>
                {errors.submit && (
                  <Box
                    sx={{
                      mb: 2,
                      px: "12px",
                      py: "10px",
                      borderRadius: "8px",
                      backgroundColor: TOKENS.dangerBg,
                      color: "#A11228",
                      fontSize: 12.5,
                      border: `1px solid #F7C9CF`,
                    }}
                  >
                    {errors.submit}
                  </Box>
                )}

                {target.id && (
                  <Box
                    sx={{
                      mb: 2,
                      px: "12px",
                      py: "8px",
                      borderRadius: "8px",
                      backgroundColor: TOKENS.bgSunken,
                      border: `1px solid ${TOKENS.borderSubtle}`,
                      fontFamily:
                        '"SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                      fontSize: 11,
                      color: TOKENS.fg3,
                    }}
                  >
                    {target.id}
                  </Box>
                )}

                <Stack spacing={2}>
                  <Box>
                    <FieldLabel required>Organisation title</FieldLabel>
                    <TextField
                      type="text"
                      name="title"
                      value={values.title}
                      placeholder="Acme Inc."
                      fullWidth
                      autoFocus
                      error={Boolean(touched.title && errors.title)}
                      helperText={touched.title && errors.title}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      sx={inputSx(false)}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: TOKENS.fg3,
                      mt: 0.5,
                    }}
                  >
                    Only the organisation title can be edited here. Use the
                    organisation drawer to manage plan, admins, and sites.
                  </Typography>
                </Stack>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  px: "24px",
                  py: "16px",
                  backgroundColor: TOKENS.bgSunken,
                  borderTop: `1px solid ${TOKENS.borderSubtle}`,
                }}
              >
                <Button
                  type="button"
                  onClick={onClose}
                  disableElevation
                  sx={{
                    height: 40,
                    px: 2,
                    borderRadius: "8px",
                    border: `1px solid ${TOKENS.border}`,
                    backgroundColor: "transparent",
                    color: TOKENS.fg1,
                    fontSize: 13,
                    fontWeight: 500,
                    textTransform: "none",
                    "&:hover": { backgroundColor: TOKENS.bgSunken, borderColor: TOKENS.fg3 },
                  }}
                  startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disableElevation
                  disabled={!canSubmit}
                  sx={{
                    height: 40,
                    px: 2,
                    borderRadius: "8px",
                    backgroundColor: TOKENS.primary,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { backgroundColor: TOKENS.primaryHover },
                    "&.Mui-disabled": {
                      backgroundColor: TOKENS.primary,
                      color: "#fff",
                      opacity: 0.5,
                      cursor: "default",
                    },
                  }}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={14} sx={{ color: "#fff" }} />
                    ) : (
                      <SaveIcon sx={{ fontSize: 16 }} />
                    )
                  }
                >
                  {isSubmitting ? "Saving…" : "Save changes"}
                </Button>
              </Box>
            </form>
          </Dialog>
        );
      }}
    </Formik>
  );
}

export default EditOrganisationModal;
